import React, { useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { useTheme } from '../styles/ThemeProvider';
import { FileUpload } from '../components/FileUpload';
import { ValidationResults } from '../components/ValidationResults';
import { SpecType, ValidationResults as ValidationResultsType, FileSpec, AIInsight, CommonIssue, LLMSettings, LLMJudgeMetrics, DEFAULT_LLM_SETTINGS } from '../types';
import { getHpsSpec, calculateHealthScore, generateAIInsights, getCommonIssues } from '../services/validationService';
import { validateFileContent } from '../services/guardrailsService';
import { api } from '../services/api';
import { Theme } from '../styles/theme';

const MainContent = styled.main`
  width: 100%;
  max-width: 1760px;
  margin: 0 auto;
  padding: 32px clamp(20px, 3vw, 48px);

  @media (max-width: 640px) {
    padding: 24px 16px;
  }
`;

const Title = styled.h2<{ color: string }>`
  font-size: 28px;
  font-weight: 600;
  color: ${props => props.color};
  margin-bottom: 8px;
`;

const Subtitle = styled.p<{ color: string }>`
  font-size: 16px;
  color: ${props => props.color};
  margin-bottom: 32px;
`;

const ErrorBanner = styled.div`
  background: #fff3f3;
  border: 1px solid #e53e3e;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 24px;
  color: #c53030;
  font-size: 14px;
`;

const LoadingOverlay = styled.div<{ colors: Theme['colors'] }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const LoadingCard = styled.div`
  background: white;
  padding: 48px;
  border-radius: 16px;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
`;

const Spinner = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  border: 4px solid ${props => props.color}20;
  border-top-color: ${props => props.color};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p<{ color: string }>`
  font-size: 16px;
  color: ${props => props.color};
`;

export const FileValidatorPage: React.FC = () => {
  const theme = useTheme();
  const [file, setFile] = useState<File | null>(null);
  const [specType, setSpecType] = useState<SpecType>('claimstatus30');
  const [results, setResults] = useState<ValidationResultsType | null>(null);
  const [spec, setSpec] = useState<FileSpec | null>(null);
  const [insights, setInsights] = useState<AIInsight[]>([]);
  const [commonIssues, setCommonIssues] = useState<CommonIssue[]>([]);
  const [healthScore, setHealthScore] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [llmSettings, setLlmSettings] = useState<LLMSettings>(DEFAULT_LLM_SETTINGS);
  const [judgeMetrics, setJudgeMetrics] = useState<LLMJudgeMetrics | null>(null);
  const [judgeLoading, setJudgeLoading] = useState(false);
  const [guardrailError, setGuardrailError] = useState<{ category: string; message: string } | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  
  const handleFileSelect = useCallback((selectedFile: File) => {
    setFile(selectedFile);
    setResults(null);
    setSpec(null);
    setInsights([]);
    setCommonIssues([]);
    setHealthScore(0);
    setGuardrailError(null);
    setValidationError(null);
    setJudgeMetrics(null);
  }, []);
  
  const handleSpecChange = useCallback((type: SpecType) => {
    setSpecType(type);
    setFile(null);
    setResults(null);
    setSpec(null);
    setInsights([]);
    setCommonIssues([]);
    setHealthScore(0);
    setGuardrailError(null);
    setValidationError(null);
    setJudgeMetrics(null);
  }, []);
  
  const handleValidate = useCallback(async () => {
    if (!file) return;
    
    // Clear previous state
    setResults(null);
    setSpec(null);
    setInsights([]);
    setCommonIssues([]);
    setHealthScore(0);
    setGuardrailError(null);
    setValidationError(null);
    setJudgeMetrics(null);

    // ── Run client-side guardrails on file content ───────────────
    try {
      const content = await file.text();
      const guardrailResult = validateFileContent(content);
      if (!guardrailResult.passed) {
        setGuardrailError({ category: guardrailResult.category, message: guardrailResult.message });
        return;
      }
    } catch {
      // If we can't read the file as text, allow the validation to proceed
    }

    setIsLoading(true);
    
    try {
      const currentSpec = getHpsSpec(specType);
      setSpec(currentSpec);
      
      // Always validate via backend API — never use local/cached data
      const validationResults = await api.validateFile(file, specType);
      setResults(validationResults);
      
      const score = calculateHealthScore(validationResults);
      setHealthScore(score);
      
      const aiInsights = generateAIInsights(validationResults, currentSpec);
      setInsights(aiInsights);
      
      const issues = getCommonIssues(validationResults);
      setCommonIssues(issues);

      // ── Async judge evaluation ───────────────────────────────────
      setJudgeLoading(true);
      api.judgeValidation({
        totalRecords: validationResults.totalRecords,
        validRecords: validationResults.validRecords,
        errors: validationResults.errors,
        warnings: validationResults.warnings,
      }).then(metrics => {
        setJudgeMetrics(metrics);
      }).catch(() => {
        // Judge failure is non-blocking
      }).finally(() => {
        setJudgeLoading(false);
      });
    } catch (error) {
      setValidationError(error instanceof Error ? error.message : 'Validation failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [file, specType]);
  
  const handleExport = useCallback(() => {
    if (!results) return;
    
    const report = {
      timestamp: new Date().toISOString(),
      specType,
      summary: {
        totalRecords: results.totalRecords,
        validRecords: results.validRecords,
        errors: results.errors.length,
        warnings: results.warnings.length,
        healthScore
      },
      errors: results.errors,
      warnings: results.warnings,
      insights
    };
    
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `validation-report-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [results, specType, healthScore, insights]);
  
  const handleCopyErrors = useCallback(() => {
    if (!results) return;
    
    const errorText = results.errors
      .map(e => `[${e.severity.toUpperCase()}] Line ${e.line}: ${e.message} (Field: ${e.field})`)
      .join('\n');
    
    navigator.clipboard.writeText(errorText);
  }, [results]);
  
  return (
    <>
      <MainContent>
        <Title color={theme.colors.textPrimary}>Validate Data Load Files</Title>
        {validationError && (
          <ErrorBanner role="alert">
            <strong>Validation failed:</strong> {validationError}
          </ErrorBanner>
        )}
        <Subtitle color={theme.colors.textSecondary}>
          Upload your Eligibility or Claims Status file and select the specification to validate against. 
          Our AI-powered validator will analyze your data and provide detailed insights.
        </Subtitle>
        
        <FileUpload
          onFileSelect={handleFileSelect}
          onSpecChange={handleSpecChange}
          selectedFile={file}
          specType={specType}
          onValidate={handleValidate}
          isValidating={isLoading}
          llmSettings={llmSettings}
          onSettingsChange={setLlmSettings}
          judgeMetrics={judgeMetrics}
          judgeLoading={judgeLoading}
          guardrailError={guardrailError}
        />
        
        {results && spec && (
          <ValidationResults
            results={results}
            spec={spec}
            insights={insights}
            commonIssues={commonIssues}
            healthScore={healthScore}
            onExport={handleExport}
            onCopyErrors={handleCopyErrors}
          />
        )}
      </MainContent>
      
      {isLoading && (
        <LoadingOverlay colors={theme.colors}>
          <LoadingCard>
            <Spinner color={theme.colors.optumBlue} />
            <LoadingText color={theme.colors.textPrimary}>Validating your file...</LoadingText>
          </LoadingCard>
        </LoadingOverlay>
      )}
    </>
  );
};
